import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { CircleCheck, Phone } from "@tiny/icons";
import { formatPhoneNumber } from "./formatPhoneNumber";

export interface NumberCardOption {
  id: string;
  phoneNumber: string;
  /** Secondary line, e.g. a region. */
  caption?: string;
  /** Small pill next to the number, e.g. "Toll-free". */
  badge?: string;
  /** Shown but not selectable; `caption` should say why. */
  disabled?: boolean;
}

export interface NumberCardListProps {
  options: NumberCardOption[];
  selectedId?: string;
  onSelect: (id: string) => void;
  "aria-label"?: string;
}

/**
 * Selectable phone-number cards. Uses only palette tokens every MUI theme has:
 * the host themes' grey scales differ (grey[100] is near-black in one).
 */
function NumberCardList(props: NumberCardListProps) {
  const { options, selectedId, onSelect } = props;

  return (
    <Box
      role="radiogroup"
      aria-label={props["aria-label"] ?? "Phone numbers"}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: "12px",
      }}
    >
      {options.map((option) => {
        const selected = option.id === selectedId && !option.disabled;
        return (
          <ButtonBase
            key={option.id}
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => onSelect(option.id)}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "12px",
              width: "100%",
              padding: "14px 16px",
              borderRadius: "10px",
              textAlign: "left",
              border: `1px solid ${
                selected ? theme.palette.primary.main : theme.palette.divider
              }`,
              boxShadow: selected
                ? `0 0 0 1px ${theme.palette.primary.main}`
                : "none",
              backgroundColor: theme.palette.common.white,
              transition: "border-color 120ms, box-shadow 120ms",
              "&:hover": { borderColor: theme.palette.primary.main },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.hover,
                "& > *": { opacity: 0.6 },
              },
              "&.Mui-focusVisible": {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              },
            })}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: "50%",
                color: selected
                  ? theme.palette.common.white
                  : theme.palette.text.secondary,
                backgroundColor: selected
                  ? theme.palette.primary.main
                  : theme.palette.action.hover,
              })}
            >
              {selected ? (
                <CircleCheck fontSize="small" />
              ) : (
                <Phone fontSize="small" />
              )}
            </Box>
            <Box minWidth={0} flex={1}>
              <Box display="flex" alignItems="center" gap="8px">
                <Typography
                  component="span"
                  fontWeight={600}
                  fontSize={15}
                  color="text.primary"
                  noWrap
                >
                  {formatPhoneNumber(option.phoneNumber)}
                </Typography>
                {option.badge && (
                  <Box
                    component="span"
                    sx={(theme) => ({
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: "18px",
                      padding: "0 6px",
                      borderRadius: "4px",
                      color: theme.palette.text.secondary,
                      backgroundColor: theme.palette.action.hover,
                      whiteSpace: "nowrap",
                    })}
                  >
                    {option.badge}
                  </Box>
                )}
              </Box>
              {option.caption && (
                <Typography
                  component="div"
                  fontSize={13}
                  color="text.secondary"
                >
                  {option.caption}
                </Typography>
              )}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export default NumberCardList;
